import { useState } from 'react'
import type { ScoutMapAgg } from '../types'

// Team spread over round time. A single "T spacing 1450u" is close to meaningless; the CURVE is the
// read — spread wide at 20s and stacked at 35s telegraphs when the execute goes. Both sides, per
// map, from the per-second buckets the fold already carries.

type Bucket = Record<number, { sum: number; n: number }>

/** buckets → [{sec, dist}] up to the window we actually watch (the first ~45s). Sparse seconds are
 *  skipped rather than drawn as zero — a gap is "no data", not "everyone on the same pixel". */
function series(b: Bucket | undefined, maxSec = 45): { sec: number; dist: number }[] {
  if (!b) return []
  return Object.entries(b)
    .map(([sec, v]) => ({ sec: Number(sec), dist: v.n ? v.sum / v.n : 0 }))
    .filter((p) => p.sec <= maxSec && p.dist > 0)
    .sort((a, b) => a.sec - b.sec)
}

export function SpacingCurve({ maps, who }: { maps: ScoutMapAgg[]; who: string }) {
  const withCurve = maps.filter((m) => {
    const c = m.spacingCurve
    return c && (Object.keys(c.CT).length > 1 || Object.keys(c.T).length > 1)
  })
  const [mapIdx, setMapIdx] = useState(0)
  if (!withCurve.length) return null
  const m = withCurve[Math.min(mapIdx, withCurve.length - 1)]
  const ct = series(m.spacingCurve?.CT)
  const t = series(m.spacingCurve?.T)

  const W = 620
  const H = 200
  const PAD = { l: 44, r: 12, t: 12, b: 26 }
  const allSecs = [...ct, ...t].map((p) => p.sec)
  const allDist = [...ct, ...t].map((p) => p.dist)
  const maxSec = Math.max(20, ...allSecs)
  const maxDist = Math.max(1000, ...allDist)
  const x = (sec: number) => PAD.l + (sec / maxSec) * (W - PAD.l - PAD.r)
  const y = (d: number) => H - PAD.b - (d / maxDist) * (H - PAD.t - PAD.b)
  const line = (pts: { sec: number; dist: number }[]) =>
    pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.sec).toFixed(1)},${y(p.dist).toFixed(1)}`).join(' ')

  const gridY = [0, 1000, 2000, 3000].filter((g) => g <= maxDist)

  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {withCurve.map((x, i) => (
          <button
            key={x.map}
            onClick={() => setMapIdx(i)}
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: i === mapIdx ? 'var(--accent)' : 'transparent',
              color: i === mapIdx ? 'var(--accent-ink)' : 'var(--text-dim)',
            }}
          >
            {x.map.replace('de_', '')}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-3 text-[11px] text-text-faint">
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-4" style={{ background: 'var(--loss)' }} /> T
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-4" style={{ background: 'var(--accent)' }} /> CT
          </span>
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 w-full" style={{ maxWidth: W }}>
        {gridY.map((g) => (
          <g key={g}>
            <line x1={PAD.l} y1={y(g)} x2={W - PAD.r} y2={y(g)} stroke="var(--border)" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(g) + 3} textAnchor="end" fontSize="9" fill="var(--text-faint)">
              {g}
            </text>
          </g>
        ))}
        {[0, 10, 20, 30, 40].filter((s) => s <= maxSec).map((s) => (
          <text key={s} x={x(s)} y={H - PAD.b + 14} textAnchor="middle" fontSize="9" fill="var(--text-faint)">
            {s}s
          </text>
        ))}
        {t.length > 1 && <path d={line(t)} fill="none" stroke="var(--loss)" strokeWidth="1.6" />}
        {ct.length > 1 && <path d={line(ct)} fill="none" stroke="var(--accent)" strokeWidth="1.6" />}
      </svg>
      <p className="mt-2 text-[11px] text-text-faint">
        Mean distance between {who} living teammates, second by second into the round. A T line that
        spreads then collapses marks when they commit — the moment to be set for. Flat and tight all
        round is a slow default; wide throughout is loose map control.
      </p>
    </>
  )
}
